/**
 * Bet-history log widget — DOM port of GameLogWidget.
 */
import type { GameLogEntry } from '../game/gameLogManager';
import { GameLogManager } from '../game/gameLogManager';

function fmtNumber(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

function fmtWinLose(v: number): string {
  const n = Math.round(v);
  if (n > 0) return `+${n.toLocaleString('en-US')}`;
  if (n < 0) return n.toLocaleString('en-US');
  return '0';
}

function fmtTime(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number): string => n.toString().padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export class GameLogWidget {
  private readonly root: HTMLElement;
  private readonly button: HTMLElement;
  private readonly badge: HTMLElement;
  private readonly panel: HTMLElement;
  private readonly list: HTMLElement;
  private readonly clearBtn: HTMLElement;
  private readonly emptyMsg: HTMLElement;
  private isExpanded = false;

  constructor() {
    const host = document.getElementById('dom-hud');
    if (!host) throw new Error('GameLogWidget: #dom-hud missing');

    this.root = document.createElement('div');
    this.root.className = 'log-widget';

    this.button = document.createElement('button');
    this.button.className = 'log-button';
    this.button.setAttribute('aria-label', 'Show bet history');
    this.button.innerHTML = `
      <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
        <path d="M13 3a9 9 0 1 0 9 9h-2a7 7 0 1 1-7-7V3z"/>
        <path d="M13 3v6l4 2-1 1.732L11 9.5V3h2z"/>
      </svg>
    `;
    this.badge = document.createElement('span');
    this.badge.className = 'log-badge';
    this.badge.textContent = '0';
    this.button.appendChild(this.badge);
    this.button.addEventListener('click', () => this.toggle());

    this.panel = document.createElement('div');
    this.panel.className = 'log-panel';

    const header = document.createElement('div');
    header.className = 'log-panel-header';
    const title = document.createElement('span');
    title.className = 'log-panel-title';
    title.textContent = 'Game Log';
    this.clearBtn = document.createElement('button');
    this.clearBtn.className = 'log-clear';
    this.clearBtn.setAttribute('aria-label', 'Clear history');
    this.clearBtn.textContent = '🗑';
    this.clearBtn.addEventListener('click', () => GameLogManager.clear());
    header.append(title, this.clearBtn);

    this.emptyMsg = document.createElement('div');
    this.emptyMsg.className = 'log-empty';
    this.emptyMsg.textContent = 'No game history yet';

    this.list = document.createElement('div');
    this.list.className = 'log-list';

    this.panel.append(header, this.emptyMsg, this.list);

    this.root.append(this.button, this.panel);
    host.appendChild(this.root);

    GameLogManager.on(() => this.render());
    this.render();
  }

  toggle(): void {
    this.isExpanded = !this.isExpanded;
    this.root.classList.toggle('expanded', this.isExpanded);
    this.button.classList.toggle('active', this.isExpanded);
  }

  collapse(): void {
    if (!this.isExpanded) return;
    this.toggle();
  }

  private render(): void {
    const entries = GameLogManager.entries;
    const count = entries.length;

    this.badge.textContent = String(count);
    this.badge.style.display = count > 0 ? '' : 'none';
    this.button.classList.toggle('has-entries', count > 0);

    this.clearBtn.style.visibility = count > 0 ? 'visible' : 'hidden';
    this.emptyMsg.style.display = count > 0 ? 'none' : '';

    this.list.innerHTML = '';
    for (const e of entries) this.list.appendChild(this.renderEntry(e));
  }

  private renderEntry(e: GameLogEntry): HTMLElement {
    const row = document.createElement('div');
    row.className = 'log-entry';

    const top = document.createElement('div');
    top.className = 'log-entry-top';
    const result = document.createElement('span');
    result.className = 'log-entry-result';
    result.textContent = e.gameResult || '—';
    const time = document.createElement('span');
    time.className = 'log-entry-time';
    time.textContent = fmtTime(e.timestamp);
    top.append(result, time);

    const mid = document.createElement('div');
    mid.className = 'log-entry-mid';
    if (e.betDetails && e.betDetails.length > 0) {
      mid.textContent = e.betDetails;
    } else if (e.betAmount > 0) {
      mid.textContent = `Bet: ${fmtNumber(e.betAmount)}`;
    } else {
      mid.textContent = 'No bet';
      mid.classList.add('muted');
    }

    const bot = document.createElement('div');
    bot.className = 'log-entry-bot';
    const wl = document.createElement('span');
    wl.className =
      'log-wl ' +
      (e.winLoseAmount > 0 ? 'win' : e.winLoseAmount < 0 ? 'lose' : 'neutral');
    wl.textContent = `W/L ${fmtWinLose(e.winLoseAmount)}`;
    const bal = document.createElement('span');
    bal.className = 'log-bal';
    bal.textContent = `$${fmtNumber(e.balanceAfter)}`;
    bot.append(wl, bal);

    row.append(top, mid, bot);
    return row;
  }
}
